import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import useAuthImageSrc from 'app/hooks/useAuthImageSrc';
import useInView from 'app/hooks/useInView';
import { tokens } from 'app/components/MatxTheme/themeColors';

const DEFAULT_FALLBACK_SRC =
  '/assets/images/argyle/newLogo/Argyle Logo - 01.svg';

// `lazy` defers resolution until the <img> nears the viewport — use it for
// gallery grids where many tiles mount at once. Single images (avatars, cards)
// should leave it off so they resolve immediately.
//
// `loader` opts a photo surface into a spinner: while the authed image is
// still resolving (or waiting to scroll into view) it shows a CircularProgress
// instead of the Argyle logo placeholder. Once resolution errors out it falls
// back to the logo. Leave it off for avatars/cards where a spinner would look
// out of place.
export default function AuthImg({
  src,
  fallbackSrc,
  lazy = false,
  loader = false,
  ...props
}) {
  const [viewRef, inView] = useInView();
  const fallback = fallbackSrc || DEFAULT_FALLBACK_SRC;
  const { resolvedSrc, error } = useAuthImageSrc(src, {
    fallbackSrc: fallback,
    enabled: !lazy || inView,
  });

  if (loader) {
    const hasImage = Boolean(resolvedSrc) && resolvedSrc !== fallback;
    const showSpinner = !hasImage && !error && Boolean(src);
    return (
      <Box
        ref={lazy ? viewRef : undefined}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {showSpinner ? (
          <CircularProgress
            size={24}
            thickness={4}
            sx={{ color: tokens.grey[400] }}
          />
        ) : (
          <img
            {...props}
            src={resolvedSrc || undefined}
            alt={props.alt || ''}
            loading="lazy"
            decoding="async"
          />
        )}
      </Box>
    );
  }

  return (
    <img
      {...props}
      ref={lazy ? viewRef : undefined}
      src={resolvedSrc || undefined}
      alt={props.alt || ''}
      loading="lazy"
      decoding="async"
    />
  );
}
